import { useLayoutEffect, useMemo, useRef } from 'react'
import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import { getSharedMeasureContext } from '@/lib/shapes/shape-text-measure'
import { measureFontAscentDescentPt } from '@/lib/text/canvas-font-metrics'
import { effectivePointSizeForRun } from '@/lib/text/text-run-vertical-align'
import {
  type FontInkPt,
  caretWidthPt as computeCaretWidthPt,
  rowBaselineOffsetPt,
  runAtCaret,
} from '../helpers/caret-geometry'
import type {
  ShapeTextCharRunMap,
  ShapeTextChunk,
  ShapeTextFonts,
  ShapeTextLayoutState,
} from '../types/shape-text-layout-state'

/** The caret bar's size and its device-pixel x snap. */
export function useShapeTextCaret({
  charRunMap,
  chunks,
  caretIndex,
  paragraphs,
  pointSize,
  familyId,
  fonts,
  canvasZoom,
  caretX,
  caretYTop,
  caretRowHeight,
  rowMetricPt,
  shapeRotation,
  isFocused,
}: {
  charRunMap: ShapeTextCharRunMap
  chunks: readonly ShapeTextChunk[]
  caretIndex: number
  paragraphs: Paragraph[]
  pointSize: ShapeTextLayoutState['pointSize']
  familyId: ShapeTextLayoutState['familyId']
  fonts: ShapeTextFonts
  canvasZoom: number
  caretX: number | undefined
  caretYTop: number | undefined
  /** The layout's height for the caret's row (`activeCaret.rowHeight`). */
  caretRowHeight: number | undefined
  rowMetricPt: ShapeTextLayoutState['rowMetricPt']
  shapeRotation: number | undefined
  isFocused: boolean
}) {
  /**
   * The caret's own ink box height — `fontBoundingBoxAscent + Descent` for the run the caret sits
   * in, which is exactly what CSS `line-height` distributes around and therefore exactly the
   * vertical extent of the glyphs beside it (see `measureFontAscentDescentPt`).
   *
   * The style comes from `runAtCaret`. Without the seeded-run fallback there, the measure below
   * lost `fontId` on an empty frame and sized the bar from generic metrics, so the caret changed
   * height on the first keystroke (WMPCM-11367).
   */
  const caretInk = useMemo(
    () => measureRunInk(runAtCaret(charRunMap, caretIndex, paragraphs), pointSize, familyId, fonts),
    [charRunMap, caretIndex, paragraphs, pointSize, familyId, fonts]
  )

  /**
   * Where the caret's row baseline sits below the row top, read from the same chunk the browser
   * paints (see `rowBaselineOffsetPt`): every run in it hangs from the baseline the TALLEST run sets,
   * so a caret in a smaller run beside a larger one has to hang from it too (WMPCM-11367). Undefined
   * until a chunk on the caret's row is found (an empty frame), where centring the ink is right.
   */
  const rowBaselinePt = useMemo(() => {
    if (caretYTop === undefined) return undefined

    const rowChunks = chunks.filter((c) => c.yTop === caretYTop)
    const before = caretIndex - 1
    const chunk =
      rowChunks.find(
        (c) => before >= c.startCharIndex && before < c.startCharIndex + c.text.length
      ) ??
      rowChunks.find(
        (c) => caretIndex >= c.startCharIndex && caretIndex <= c.startCharIndex + c.text.length
      )

    if (!chunk) return undefined

    const inkByRun = new Map<string, FontInkPt>()

    for (let i = 0; i < chunk.text.length; i++) {
      const run = charRunMap[chunk.startCharIndex + i] ?? null
      const key = `${run?.fontFamilyId ?? ''}|${run?.fontId ?? ''}|${effectivePointSizeForRun(run, pointSize)}`

      if (!inkByRun.has(key)) inkByRun.set(key, measureRunInk(run, pointSize, familyId, fonts))
    }

    return rowBaselineOffsetPt(chunk.rowHeight, [...inkByRun.values()]) ?? undefined
  }, [chunks, caretYTop, caretIndex, charRunMap, pointSize, familyId, fonts])

  /**
   * The row height to place the caret in when its row holds no text: an empty line, such as one a
   * deletion emptied. The layout sizes a row from the characters on it, and with none it carries
   * over the line above's height, so a caret on an empty 12pt line under 32pt text was centred in a
   * 36pt row and then jumped up ~12px into the real 14.4pt row on the first keystroke (WMPCM-11367).
   * The line's own run sizes it the way typing will, so it is NOT capped by the layout's height: that
   * is only a carry-over, and it can be smaller too (a 72pt empty line under an empty 18pt one got a
   * 21.6pt row, and the caret was clamped to 18pt's height). The shape's own height still bounds the
   * bar (`caretVerticalBox`). Undefined when the row has text.
   */
  const caretRowHeightPt = useMemo(() => {
    if (caretYTop === undefined || caretRowHeight === undefined) return undefined
    if (chunks.some((c) => c.yTop === caretYTop)) return undefined

    const run = runAtCaret(charRunMap, caretIndex, paragraphs)

    return rowMetricPt(run ?? undefined)
  }, [chunks, caretYTop, caretRowHeight, caretIndex, charRunMap, paragraphs, rowMetricPt])

  const devicePixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  const caretWidthPt = computeCaretWidthPt(devicePixelRatio, canvasZoom)
  const caretRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const el = caretRef.current

    if (!el) return
    el.style.translate = ''

    // Only an unrotated frame has a screen-axis-aligned x to snap.
    if (shapeRotation) return

    const rect = el.getBoundingClientRect()
    const shiftPx = Math.round(rect.x * devicePixelRatio) / devicePixelRatio - rect.x

    if (Math.abs(shiftPx) > 1e-3) el.style.translate = `${shiftPx / canvasZoom}px 0`
  }, [caretX, caretYTop, canvasZoom, caretWidthPt, devicePixelRatio, shapeRotation, isFocused])

  return { caretRef, caretInk, rowBaselinePt, caretRowHeightPt, caretWidthPt }
}

/** A run's font-wide ascent and descent: what CSS `line-height` distributes around its glyphs. */
function measureRunInk(
  run: TextRun | null,
  pointSize: number,
  familyId: number | undefined,
  fonts: ShapeTextFonts
): FontInkPt {
  return measureFontAscentDescentPt(
    getSharedMeasureContext(),
    effectivePointSizeForRun(run, pointSize),
    run?.fontFamilyId ?? familyId,
    run?.fontId,
    fonts ?? undefined
  )
}
